import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import classnames from 'classnames';
import { padCharsStart } from 'lodash/fp';

import { Button } from '../button/Button';
import styles from './Modal.module.scss';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { extendToken, getLogoutWarningModalOpen, logoutStarted } from '../../auth/login/store';
import { ModalHeader } from './ModalHeader';
import { User } from '../icons';

const { red, amber } = styles;

const ONE_MINUTE = 60;
const FIVE_MINUTE_TIMER = 5 * ONE_MINUTE;

const height = 150;
const width = 150;
const radius = 50;
const circumference = 2 * Math.PI * radius;
const cx = width / 2;
const cy = height / 2;

export const LogoutWarningModal: React.FC = () => {
    const [logoutTimer, setLogoutTimer] = useState<number>(FIVE_MINUTE_TIMER);
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(getLogoutWarningModalOpen);
    const extend = useCallback(() => {
        dispatch(extendToken());
        setLogoutTimer(FIVE_MINUTE_TIMER);
    }, [dispatch]);

    useEffect(() => {
        if (logoutTimer > 0 && isOpen) {
            const logoutCountdown = setInterval(() => {
                setLogoutTimer(logoutTimer - 1);
            }, 1000);
            return () => clearTimeout(logoutCountdown);
        }
        return () => setLogoutTimer(FIVE_MINUTE_TIMER);
    }, [logoutTimer, isOpen]);

    useEffect(() => {
        if (logoutTimer === 0) {
            dispatch(logoutStarted());
        }
    }, [logoutTimer, dispatch]);

    const logoutCountdown = useMemo(() => `${Math.floor(logoutTimer / ONE_MINUTE)}:${padCharsStart('0', 2, `${logoutTimer % 60}`)}`, [logoutTimer]);
    const offset = useMemo(() => (FIVE_MINUTE_TIMER - logoutTimer) / FIVE_MINUTE_TIMER * circumference, [logoutTimer]);

    return (
        <Modal
            isOpen={isOpen}
            className={styles.modal}
            ariaHideApp={false}
            style={{ overlay: { display: 'flex', zIndex: 100 } }}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
        >
            <div className={styles.logoutWarningWrapper}>
                <ModalHeader label='Are you still there?' icon={User} testId='logout-warning-modal' />
                <div className={styles.logoutWarningMessage} style={{ color: logoutTimer <= ONE_MINUTE ? red : amber }} data-testid='logout-warning-modal-message'>You will be logged out shortly due to inactivity</div>
                <div className={styles.logoutTimerMessage} data-testid='logout-timer-message'>
                    <div className={styles.logoutTimer}>{logoutCountdown}</div>
                    <div className={styles.logoutSpinner}>
                        <svg width={width} height={height}>
                            <circle
                                className={styles.staticCircle}
                                r={radius}
                                cx={cx}
                                cy={cy}
                                fill='none'
                                strokeDasharray={circumference}
                            />
                            <circle
                                className={classnames(styles.logoutCountdown, {
                                    [styles.logoutAmberCountdown]: logoutTimer <= 2 * ONE_MINUTE && logoutTimer > ONE_MINUTE,
                                    [styles.logoutRedCountdown]: logoutTimer <= ONE_MINUTE
                                })}
                                r={radius}
                                cx={cx}
                                cy={cy}
                                fill='none'
                                strokeDasharray={circumference}
                                strokeDashoffset={offset}
                            />
                        </svg>
                    </div>
                </div>
                <div className={styles.buttonWrapper}>
                    <Button onClick={extend} label='Stay Logged In' testId='logout-warning-modal' />
                </div>
            </div>
        </Modal>
    );
};
